/**
 * Builder progress messages displayed as synthetic thinking messages
 * when the agent is working and no real thinking messages arrive for 10+ seconds.
 *
 * Messages are organized by phase and rotate to give users a sense of progress.
 * These are purely frontend UX constructs — not persisted, gone on refresh.
 *
 * All message text is stored in i18n (automation namespace) under
 * `automation.builderProgress.phase.*` and `automation.builderProgress.insight.*`.
 */

/** A single progress message entry referencing an i18n key. */
export interface IProgressMessage {
  /** The i18n key for the message text (automation namespace) */
  key: string;
}

/** A phase containing a set of progress messages. */
export interface IProgressPhase {
  /** Phase label for internal tracking */
  label: string;
  /** i18n keys for status messages to rotate through */
  messages: IProgressMessage[];
}

/**
 * Insight ticker messages that rotate during longer waits.
 * Mixed in between phase messages every few cycles to keep content fresh.
 */
export const INSIGHT_TICKER_MESSAGES: IProgressMessage[] = [
  { key: 'automation.builderProgress.insight.1' },
  { key: 'automation.builderProgress.insight.2' },
  { key: 'automation.builderProgress.insight.3' },
  { key: 'automation.builderProgress.insight.4' },
  { key: 'automation.builderProgress.insight.5' },
  { key: 'automation.builderProgress.insight.6' },
  { key: 'automation.builderProgress.insight.7' },
  { key: 'automation.builderProgress.insight.8' },
  { key: 'automation.builderProgress.insight.9' },
  { key: 'automation.builderProgress.insight.10' },
  { key: 'automation.builderProgress.insight.11' },
  { key: 'automation.builderProgress.insight.12' },
  { key: 'automation.builderProgress.insight.13' },
  { key: 'automation.builderProgress.insight.14' },
  { key: 'automation.builderProgress.insight.15' },
  { key: 'automation.builderProgress.insight.16' },
  { key: 'automation.builderProgress.insight.17' },
  { key: 'automation.builderProgress.insight.18' },
  { key: 'automation.builderProgress.insight.19' },
  { key: 'automation.builderProgress.insight.20' },
  { key: 'automation.builderProgress.insight.21' },
  { key: 'automation.builderProgress.insight.22' },
  { key: 'automation.builderProgress.insight.23' },
  { key: 'automation.builderProgress.insight.24' },
];

/** Minimum gap (ms) before synthetic messages start appearing. */
export const PROGRESS_GAP_THRESHOLD_MS = 10_000;

/** Interval (ms) between synthetic messages during normal phases. */
export const PROGRESS_MESSAGE_INTERVAL_MS = 10_000;

/** Faster interval (ms) for insight ticker during long waits (>30s). */
export const PROGRESS_FAST_INTERVAL_MS = 6_000;

/** Threshold (ms) after which we switch to faster ticker rotation. */
export const PROGRESS_LONG_WAIT_THRESHOLD_MS = 30_000;
